import React, { useState } from 'react';
import { Row, Col, Card, Typography, Modal, Form, DatePicker, Select, Button, Space, theme, Divider, message } from 'antd';
import {
    BarChart3,
    Wallet,
    CreditCard,
    TrendingUp,
    FileSpreadsheet,
    ArrowRight,
    ShieldCheck,
    Building2,
    Users,
    Grid,
    Clock,
    Scissors,
    AlertCircle,
    Target,
    Activity,
    History,
    FileText,
    Zap
} from 'lucide-react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { authenticatedFetch, API_URL } from '../utils/api';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const REPORTS_LIST = [
    {
        id: 'revenue_report',
        title: 'Revenue Summary',
        description: 'Detailed monthly revenue breakdown across all facilities and clients with tax splits.',
        icon: <BarChart3 size={24} />,
        color: '#6366f1',
        bgColor: '#eef2ff',
        filters: ['dateRange', 'facility']
    },
    {
        id: 'collection_report',
        title: 'Collection vs Receivable',
        description: 'Track actual cash inflow against raised invoices, including TDS and pending dues.',
        icon: <Wallet size={24} />,
        color: '#22c55e',
        bgColor: '#f0fdf4',
        filters: ['dateRange', 'facility']
    },
    {
        id: 'expense_report',
        title: 'Operational Expenses',
        description: 'Itemized tracker for all operational costs categorized by service and facility.',
        icon: <CreditCard size={24} />,
        color: '#ef4444',
        bgColor: '#fef2f2',
        filters: ['dateRange', 'facility']
    },
    {
        id: 'client_performance',
        title: 'Client Performance',
        description: 'Ranking and historical revenue analysis for all active and legacy clients.',
        icon: <Users size={24} />,
        color: '#f59e0b',
        bgColor: '#fffbeb',
        filters: ['facility']
    },
    {
        id: 'gst_register',
        title: 'GST Sales Register',
        description: 'Consolidated tax report containing CGST, SGST and IGST totals for auditing.',
        icon: <ShieldCheck size={24} />,
        color: '#06b6d4',
        bgColor: '#ecfeff',
        filters: ['dateRange']
    },
    {
        id: 'aging_report',
        title: 'Aged Receivables',
        description: 'Analysis of outstanding invoices grouped by age (0-30, 31-60, 61-90, 90+ days).',
        icon: <Clock size={24} />,
        color: '#8b5cf6',
        bgColor: '#f5f3ff',
        filters: ['facility']
    },
    {
        id: 'tds_tracker',
        title: 'TDS Deduction Tracker',
        description: 'Focus on TDS amounts withheld by clients to track against Form 26AS.',
        icon: <Scissors size={24} />,
        color: '#ec4899',
        bgColor: '#fdf2f8',
        filters: ['dateRange', 'facility']
    },
    {
        id: 'outstanding_payables',
        title: 'Outstanding Payables',
        description: 'Track all pending vendor payments grouped by due date and vendor priority.',
        icon: <AlertCircle size={24} />,
        color: '#f43f5e',
        bgColor: '#fff1f2',
        filters: ['facility']
    },
    {
        id: 'pl_facility',
        title: 'Profit & Loss (Facility)',
        description: 'Facility-wise financial performance comparing localized revenue against operational spend.',
        icon: <Target size={24} />,
        color: '#10b981',
        bgColor: '#ecfdf5',
        filters: ['dateRange', 'facility']
    },
    {
        id: 'gst_recon',
        title: 'GST Reconciliation',
        description: 'Comparison of GST collected (Output) vs GST paid to vendors (Input) for tax filing.',
        icon: <ShieldCheck size={24} />,
        color: '#0ea5e9',
        bgColor: '#f0f9ff',
        filters: ['dateRange']
    },
    {
        id: 'msme_report',
        title: 'MSME Compliance',
        description: 'Identify outstanding payments to MSME vendors to track 45-day statutory compliance.',
        icon: <Clock size={24} />,
        color: '#d946ef',
        bgColor: '#fdf4ff',
        filters: ['facility']
    },
    {
        id: 'capex_opex',
        title: 'Capex vs Opex',
        description: 'Categorization of expenses into Capital vs Operating expenditure for tax planning.',
        icon: <Activity size={24} />,
        color: '#f97316',
        bgColor: '#fff7ed',
        filters: ['dateRange', 'facility']
    },
    {
        id: 'lead_funnel',
        title: 'Lead Conversion Funnel',
        description: 'Track conversion metrics from initial Enquiry to Quotation and final Term Sheet.',
        icon: <Zap size={24} />,
        color: '#6366f1',
        bgColor: '#eef2ff',
        filters: ['dateRange']
    },
    {
        id: 'user_audit',
        title: 'User Action Audit',
        description: 'Complete trail of system actions like invoice cancellations or payment edits by user.',
        icon: <History size={24} />,
        color: '#475569',
        bgColor: '#f1f5f9',
        filters: ['dateRange']
    },
    {
        id: 'credit_usage',
        title: 'Credit Note Utilization',
        description: 'Track issued credit notes and verify their application status against invoices.',
        icon: <FileText size={24} />,
        color: '#ef4444',
        bgColor: '#fef2f2',
        filters: ['dateRange', 'facility']
    },
    {
        id: 'lead_roi',
        title: 'Lead Source ROI',
        description: 'Analyzes which lead sources (Google, Referral, etc.) are bringing in the highest revenue.',
        icon: <Users size={24} />,
        color: '#fbbf24',
        bgColor: '#fffbeb',
        filters: ['dateRange']
    }
];

const Reports = ({ facilities = [], clients = [] }) => {
    const { token } = theme.useToken();
    const [selectedReport, setSelectedReport] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const handleCardClick = (report) => {
        setSelectedReport(report);
        setIsModalOpen(true);
        form.setFieldsValue({
            dateRange: [dayjs().startOf('year'), dayjs()],
            facility: undefined
        });
    };

    const handleDownload = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const queryParams = new URLSearchParams();
            if (values.dateRange) {
                queryParams.append('startDate', values.dateRange[0].format('YYYY-MM-DD'));
                queryParams.append('endDate', values.dateRange[1].format('YYYY-MM-DD'));
            }
            if (values.facility) queryParams.append('facility', values.facility);
            queryParams.append('reportType', selectedReport.id);

            const response = await authenticatedFetch(`${API_URL}/reports/download?${queryParams.toString()}`);

            if (!response || response.message) {
                throw new Error(response?.message || 'Failed to fetch report data');
            }

            if (response.data && response.data.length > 0) {
                const worksheet = XLSX.utils.json_to_sheet(response.data);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
                XLSX.writeFile(workbook, `${selectedReport.title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.xlsx`);
                message.success(`${selectedReport.title} downloaded successfully!`);
                setIsModalOpen(false);
            } else {
                message.info('No data found for the selected criteria.');
            }
        } catch (error) {
            console.error('Report Download Error:', error);
            message.error(error.message || 'Error generating report');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-container" style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
            <div style={{ marginBottom: '32px' }}>
                <Title level={2} style={{ margin: 0, color: '#1e293b' }}>Reports Center</Title>
                <Text type="secondary">Generate and download comprehensive Excel reports for business intelligence</Text>
            </div>

            <Row gutter={[24, 24]}>
                {REPORTS_LIST.map((report) => (
                    <Col xs={24} sm={12} lg={8} key={report.id}>
                        <Card
                            hoverable
                            bordered={false}
                            onClick={() => handleCardClick(report)}
                            style={{
                                borderRadius: '16px',
                                height: '100%',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                transition: 'all 0.3s ease'
                            }}
                            bodyStyle={{ padding: '24px', display: 'flex', flexDirection: 'column', height: '100%' }}
                        >
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                background: report.bgColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '20px',
                                color: report.color
                            }}>
                                {report.icon}
                            </div>

                            <Title level={4} style={{ marginBottom: '8px', color: '#334155' }}>{report.title}</Title>
                            <Paragraph type="secondary" style={{ flex: 1, marginBottom: '20px' }}>
                                {report.description}
                            </Paragraph>

                            <Divider style={{ margin: '0 0 16px 0' }} />

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Space>
                                    <FileSpreadsheet size={16} color="#94a3b8" />
                                    <Text style={{ fontSize: '13px', color: '#64748b' }}>Excel Export</Text>
                                </Space>
                                <ArrowRight size={18} color={token.colorPrimary} />
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Modal
                title={
                    <Space>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: selectedReport?.bgColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: selectedReport?.color
                        }}>
                            {selectedReport?.icon}
                        </div>
                        <span>Configure {selectedReport?.title}</span>
                    </Space>
                }
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalOpen(false)}>Cancel</Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<FileSpreadsheet size={16} />}
                        loading={loading}
                        onClick={handleDownload}
                        style={{ borderRadius: '6px' }}
                    >
                        Generate Excel
                    </Button>
                ]}
                width={500}
                centered
                styles={{ body: { padding: '24px 0' } }}
            >
                <Form form={form} layout="vertical" style={{ padding: '0 24px' }}>
                    {selectedReport?.filters.includes('dateRange') && (
                        <Form.Item name="dateRange" label="Reporting Period" rules={[{ required: true, message: 'Please select a period' }]}>
                            <RangePicker style={{ width: '100%' }} />
                        </Form.Item>
                    )}

                    {selectedReport?.filters.includes('facility') && (
                        <Form.Item name="facility" label="Target Facility">
                            <Select placeholder="All Facilities" allowClear>
                                {facilities.map(f => (
                                    <Select.Option key={f.id} value={f.name}>{f.name}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <div style={{
                        background: '#f1f5f9',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        marginTop: '8px'
                    }}>
                        <Space align="start">
                            <div style={{ marginTop: '4px' }}>
                                <Grid size={16} color="#64748b" />
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Your report will be generated as a multi-column Excel workbook compatible with auditing software and financial tools.
                            </Text>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <style>{`
        .ant-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
        }
      `}</style>
        </div>
    );
};

export default Reports;
